import React, { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { ContainerCardCompact } from "../components/ContainerCardCompact";
import { PremiumLockTile } from "../components/PremiumLockTile";
import { UnusedSlotsTile } from "../components/UnusedSlotsTile";
import { LocationsCard } from "../components/LocationsCard";
import { containersApi } from "../lib/api";
import { useFilters, filtersToParams } from "../lib/FilterContext";
import { useAuth } from "../lib/AuthContext";

const Inventory = () => {
  const { user } = useAuth();
  const { filters } = useFilters();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await containersApi.list(filtersToParams(filters));
      setContainers(list);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return containers;
    const needle = q.toLowerCase();
    return containers.filter(
      (c) =>
        c.name.toLowerCase().includes(needle) ||
        c.qr_code.toLowerCase().includes(needle) ||
        (c.notes || "").toLowerCase().includes(needle)
    );
  }, [q, containers]);

  return (
    <div className="px-5 pt-6 pb-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">Inventory</div>
        <h1 className="font-heading text-3xl font-bold text-[#2D3142] mt-1">All containers</h1>
      </motion.div>

      <div className="relative mt-6">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A93A6]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, QR code, or notes"
          data-testid="inventory-search"
          className="pl-10 bg-white border-[#E2E0D8] h-11"
        />
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
          {loading ? "Loading…" : `${filtered.length} item${filtered.length === 1 ? "" : "s"}`}
        </span>
        <span className="text-xs text-[#8A93A6]">Filtered by your settings</span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3" data-testid="inventory-grid">
        <LocationsCard />
        {filtered.map((c) => (
          <ContainerCardCompact key={c.id} container={c} />
        ))}
        {!loading && <UnusedSlotsTile />}
        {!loading && !user?.is_premium && <PremiumLockTile />}
        {!loading && filtered.length === 0 && (
          <div className="col-span-2 tup-card p-8 text-center">
            <p className="text-sm text-[#4F5D75]">No containers match your search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;
